import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Odometer from 'odometer';
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  Tooltip as MuiTooltip,
  Select,
  MenuItem,
  Grow,
  Grid,
} from '@mui/material';
import { getDateRange, NoRecords } from '../../../../Helpers/Common';
import {
  fetchFinancialYears,
  fetchDashboardData,
  setSelectedFinancialYear,
  setHoveredCardIndex,
} from '../../../../Helpers/dashboardSlice';
import CategoryChart from './Charts/CategoryChart';
import ContainmentChart from './Charts/ContainmentChart';
import LocationChart from './Charts/LocationChart';
import NCTrendChart from './Charts/NCTrendChart';
import RootCauseChart from './Charts/RootCauseChart';
import { useMediaQuery } from '@mui/system';

const navigateToView = '/ncpro/view';

const RepeatedItemsBar = ({ count, ncrs }) => {
  if (count === 0) return null;

  const tooltipContent = ncrs.length > 0 ? (
    <Box sx={{ height: ncrs?.length > 10 ? '250px' : 'auto', width: 'auto', overflow: 'auto' }}>
      {ncrs?.map((ncr, idx) => (
        <Typography key={idx} variant="caption" display="block" sx={{ py: 0.25 }}>
          {ncr}
        </Typography>
      ))}
    </Box>
  ) : null;

  return (
    <Box sx={{ width: '80%' }}>
      <MuiTooltip
        title={tooltipContent || ''}
        placement="top-end"
        slotProps={{
          popper: {
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [10, -30],
                },
              },
            ],
          },
          tooltip: {
            sx: {
              bgcolor: 'white',
              color: 'black',
              border: '2px solid #1e3a8a',
              borderRadius: '4px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              fontSize: '0.875rem',
              px: 2,
              py: 1.5,
            },
          },
        }}
      >
        <Paper
          sx={{
            border: '2px solid #1e3a8a',
            borderRadius: 1,
            px: 2,
            py: 1,
            cursor: count > 0 && ncrs.length > 0 ? 'pointer' : 'default',
            transition: 'all 0.2s',
            '&:hover': {
              boxShadow: count > 0 && ncrs.length > 0 ? 2 : 0,
            },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 400 }}>
            {count > 1 ? `${count} Parts` : `${count} Part`}
          </Typography>
        </Paper>
      </MuiTooltip>
    </Box>
  );
};

export default function Dashboard({ isTogggleDashboard }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Redux state
  const dashboardData = useSelector(state => state.dashboard.data);
  const selectedFinancialYear = useSelector(state => state.dashboard.selectedFinancialYear);
  const financialYears = useSelector(state => state.dashboard.financialYears);
  const hoveredCardIndex = useSelector(state => state.dashboard.hoveredCardIndex);
  const isCheckCOPQ = useSelector(state => state.dashboard.isCheckCOPQ);
  const isCustom = useMediaQuery('(min-width:1530px) and (max-width:1600px)')

  // Odometer refs for counter animation
  const odometerRefs = useRef([]);
  const odometerInstancesRef = useRef([]);

  useEffect(() => {
    // Small delay to ensure DOM is rendered
    const timer = setTimeout(() => {
      const stats = [
        { value: dashboardData?.totalNcrs || 0 },
        { value: dashboardData?.openNcrs || 0 },
        { value: dashboardData?.closedNcrs || 0 },
        { value: dashboardData?.inAction || 0 },
        { value: dashboardData?.inReview || 0 },
        { value: dashboardData?.inClosure || 0 },
        { value: dashboardData?.actionAgingCount || 0 },
        { value: dashboardData?.reviewAgingCount || 0 }
      ];

      stats.forEach((stat, index) => {
        const el = odometerRefs.current[index];
        if (!el) return;

        // Clean up previous odometer instance if it exists
        if (odometerInstancesRef.current[index]) {
          odometerInstancesRef.current[index] = null;
        }

        const odometer = new Odometer({
          el,
          value: 0,
          theme: 'default',
        });

        odometerInstancesRef.current[index] = odometer;

        let hasRun = false;
        const observer = new IntersectionObserver(([entry]) => {
          if (entry.isIntersecting && !hasRun) {
            odometer.update(stat.value);
            hasRun = true;
            observer.disconnect();
          }
        }, { threshold: 0.8 });

        observer.observe(el);
      });
    }, 100);

    return () => {
      clearTimeout(timer);
    };
  }, [dashboardData]);

  const handleNavigateToView = () => {
    const { startDate, endDate } = getDateRange(selectedFinancialYear.startDate, selectedFinancialYear.endDate);
    const dateRangeFilter = startDate && endDate
      ? `${startDate}.${endDate}`
      : '';

    navigate(navigateToView, {
      state: {
        fromDashboard: true,
        dateRange: dateRangeFilter
      }
    });
  };

  const navigateWithFilters = (filterKey, filterValue) => {
    const { startDate, endDate } = getDateRange(selectedFinancialYear.startDate, selectedFinancialYear.endDate);
    const dateRangeFilter = startDate && endDate
      ? `${startDate}.${endDate}`
      : '';

    navigate(navigateToView, {
      state: {
        fromDashboard: true,
        dateRange: dateRangeFilter,
        additionalFilters: {
          [filterKey]: filterValue
        }
      }
    });
  };

  const handleOpenNcrsClick = () => navigateWithFilters('status', 'Open');
  const handleClosedNcrsClick = () => navigateWithFilters('status', 'Closed');
  const handleInActionClick = () => navigateWithFilters('status', 'In Action');
  const handleInReviewClick = () => navigateWithFilters('status', 'In Review');
  const handleInClosureClick = () => navigateWithFilters('status', 'In Closure');

  const handleActionAgingClick = () => {
    const { startDate, endDate } = getDateRange(selectedFinancialYear.startDate, selectedFinancialYear.endDate);
    const dateRangeFilter = startDate && endDate
      ? `${startDate}.${endDate}`
      : '';

    navigate('/ncpro/action', {
      state: {
        fromDashboard: true,
        dateRange: dateRangeFilter,
        additionalFilters: {
          status: 'In Action',
          aging: true
        }
      }
    });
  };

  const handleReviewAgingClick = () => {
    const { startDate, endDate } = getDateRange(selectedFinancialYear.startDate, selectedFinancialYear.endDate);
    const dateRangeFilter = startDate && endDate
      ? `${startDate}.${endDate}`
      : '';

    navigate('/ncpro/review', {
      state: {
        fromDashboard: true,
        dateRange: dateRangeFilter,
        additionalFilters: {
          status: 'In Review',
          aging: true
        }
      }
    });
  };

  const handleLocationClick = (data) => {
    const { startDate, endDate } = getDateRange(selectedFinancialYear.startDate, selectedFinancialYear.endDate);
    const dateRangeFilter = startDate && endDate
      ? `${startDate}.${endDate}`
      : '';

    navigate(navigateToView, {
      state: {
        fromDashboard: true,
        dateRange: dateRangeFilter,
        additionalFilters: {
          location: data.name
        }
      }
    });
  };

  const handleCategoryClick = (data) => {
    const { startDate, endDate } = getDateRange(selectedFinancialYear.startDate, selectedFinancialYear.endDate);
    const dateRangeFilter = startDate && endDate
      ? `${startDate}.${endDate}`
      : '';

    navigate(navigateToView, {
      state: {
        fromDashboard: true,
        dateRange: dateRangeFilter,
        additionalFilters: {
          category: data.name
        }
      }
    });
  };

  const handleMonthlyClick = (data) => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const monthIndex = monthNames.findIndex(m => m.toLowerCase().includes(data.fullMonth.toLowerCase().split(' ')[0].toLowerCase()));

    const [startMonth, startYear] = selectedFinancialYear.startDate.split('/').map(Number);
    let year = startYear;

    if (monthIndex < startMonth - 1) {
      year = startYear + 1;
    }

    const monthNum = monthIndex + 1;
    const monthStart = `${year}-${String(monthNum).padStart(2, '0')}-01`;
    const lastDay = new Date(year, monthNum, 0).getDate();
    const monthEnd = `${year}-${String(monthNum).padStart(2, '0')}-${lastDay}`;
    const dateRangeFilter = `${monthStart}.${monthEnd}`;

    navigate(isCheckCOPQ ? '/report/datatable' : navigateToView, {
      state: {
        fromDashboard: true,
        dateRange: dateRangeFilter,
        additionalFilters: {
          month: data.fullMonth
        }
      }
    });
  };

  // Fetch financial years and data on component mount - only if not already loaded
  useEffect(() => {
    // Only fetch if financial years are not already loaded in Redux
    if (financialYears.length === 0) {
      dispatch(fetchFinancialYears()).then((action) => {
        if (action.payload?.latestYear) {
          dispatch(fetchDashboardData({
            startDate: action.payload.latestYear.startYear,
            endDate: action.payload.latestYear.endYear
          }));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const monthOrder = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];

  const monthlyNCRData = Object.entries(dashboardData?.monthlyWiseNcrs).map(([month, count]) => ({
    month: month.substring(0, 3),
    count,
    fullMonth: month,
  }))?.sort((a, b) => {
    return monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month);
  })?.filter(item => item.count !== 'future');

  const monthlyCOPQData = Object.entries(dashboardData?.monthlyWiseCOPQ).map(([month, count]) => ({
    month: month.substring(0, 3),
    count,
    fullMonth: month,
  }))?.sort((a, b) => {
    return monthOrder.indexOf(a.month) - monthOrder.indexOf(b.month);
  })?.filter(item => item.count !== 'future');

  const locationData = Object.entries(dashboardData?.locationWiseNcrs).map(([name, data]) => ({
    name,
    value: data.count,
    percentage: data.percentage,
  }));

  const categoryData = Object.entries(dashboardData?.categoryWiseNcrs).map(([name, data]) => ({
    name,
    value: data.count,
    percentage: data.percentage,
  }));

  const containmentData = Object.entries(dashboardData?.containmentActionWiseNcrs).map(([name, count]) => ({
    name,
    count,
  }));

  const rootCauseData = dashboardData?.rootCauseWiseNcrs
    ?.map((item) => ({
      name: item.lookup_value,
      count: parseInt(item.count) || 0,
    }))
    ?.sort((a, b) => b.count - a.count);

  const repeatedItemsData = [
    { label: '>5x', count: dashboardData?.repeatedItemsWiseNcrs.ncrsMorethantfive.count, ncrs: dashboardData?.repeatedItemsWiseNcrs.ncrsMorethantfive.ncrs },
    { label: '5x', count: dashboardData?.repeatedItemsWiseNcrs.ncrs_5X.count, ncrs: dashboardData?.repeatedItemsWiseNcrs.ncrs_5X.ncrs },
    { label: '4x', count: dashboardData?.repeatedItemsWiseNcrs.ncrs_4X.count, ncrs: dashboardData?.repeatedItemsWiseNcrs.ncrs_4X.ncrs },
    { label: '3x', count: dashboardData?.repeatedItemsWiseNcrs.ncrs_3X.count, ncrs: dashboardData?.repeatedItemsWiseNcrs.ncrs_3X.ncrs },
    { label: '2x', count: dashboardData?.repeatedItemsWiseNcrs.ncrs_2X.count, ncrs: dashboardData?.repeatedItemsWiseNcrs.ncrs_2X.ncrs },
  ];

  const checkData = (isCheckCOPQ ? monthlyCOPQData : monthlyNCRData)?.some(item => item.count > 0);

  return (
    <div style={{ minHeight: '100vh' }}>
      <Grow in={true} timeout={800}
        style={{ transformOrigin: '0 0 0', transition: 'all 0.7s ease-in-out' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: "15px", marginBottom: '12px' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '16px' }}>
            Period
          </Typography>
          <Select
            style={{ backgroundColor: "white", width: '200px' }}
            name="Period"
            value={selectedFinancialYear?.financialYear}
            onChange={(e) => {
              dispatch(setSelectedFinancialYear({
                financialYear: e.target.value?.financialYear,
                startDate: e.target.value?.startYear,
                endDate: e.target.value?.endYear,
              }));
              dispatch(fetchDashboardData({
                startDate: e.target.value?.startYear,
                endDate: e.target.value?.endYear
              }));
            }}
            disabled={financialYears?.length === 0}
            displayEmpty
            renderValue={(selected) => {
              if (!selected) {
                return <Typography sx={{ color: 'text.secondary' }}>Choose Period</Typography>;
              }
              return selected;
            }}
            sx={{ minWidth: 200, height: 40 }}
          >
            {financialYears?.map((item) => (
              <MenuItem
                key={item?.financialYear}
                value={item}
                selected={item?.financialYear === selectedFinancialYear?.financialYear}
                sx={{
                  backgroundColor: item?.financialYear === selectedFinancialYear?.financialYear ? '#e3f2fd' : 'transparent',
                  '&.Mui-selected': {
                    backgroundColor: '#2196F3',
                    color: 'white',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#1976D2'
                    }
                  },
                  '&:hover': {
                    backgroundColor: '#e4e4e4ff'
                  }
                }}
              >
                {item?.financialYear}
              </MenuItem>
            )) || []}
          </Select>
        </div>
      </Grow>

      <Box sx={{ margin: '20px auto' }}>
        {/* Summary Cards */}
        <Grid container spacing={isCustom ? 2 : 4} sx={{ marginBottom: '34px' }}>
          {/* Total No. of NCs */}
          <Grow in={true} timeout={1000}>
            <Grid item xs={12} md={12} lg={4} xl={3}>
              <Grow
                in={true}
                style={{ transformOrigin: '0 0 0', transition: 'all 0.7s ease-in-out' }}
                timeout={1500}
              >
                <Card
                  onMouseEnter={() => dispatch(setHoveredCardIndex(0))}
                  onMouseLeave={() => dispatch(setHoveredCardIndex(null))}
                  sx={{
                    boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)",
                    height: 180,
                    display: 'flex',
                    flexDirection: 'column',
                    background: hoveredCardIndex !== null && hoveredCardIndex !== 0 ? '#ffffff' : 'linear-gradient(180deg, #1E3A8A 0%, #00BCD4 100%)',
                    color: hoveredCardIndex !== null && hoveredCardIndex !== 0 ? '#374151' : 'white',
                    borderRadius: 4,
                    transition: 'all 0.3s ease-in-out',
                    cursor: dashboardData?.totalNcrs > 0 ? 'pointer' : 'default'
                  }}
                  title={dashboardData?.totalNcrs > 0 ? 'Click to view' : ''}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pt: 2.5 }}>
                    <Typography variant="body1" sx={{ mb: 3, mt: -2, fontWeight: 600, color: hoveredCardIndex !== null && hoveredCardIndex !== 0 ? '#6b7280' : 'white' }}>
                      Cumulative NC Volume {'(YTD)'}
                    </Typography>
                    <Typography
                      onClick={() => dashboardData?.totalNcrs > 0 && handleNavigateToView()} variant="h1" sx={{ fontWeight: 700, fontSize: '4rem', cursor: dashboardData?.totalNcrs > 0 ? 'pointer' : 'default', color: hoveredCardIndex !== null && hoveredCardIndex !== 0 ? '#1f2937' : 'white' }}>
                      <span
                        ref={(el) => {
                          if (el) odometerRefs.current[0] = el;
                        }}
                      >
                        0
                      </span>
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grow>

          {/* Open vs Closed */}
          <Grow in={true} timeout={1300}>
            <Grid item xs={12} md={12} lg={4} xl={3}>
              <Grow
                in={true}
                style={{ transformOrigin: '0 0 0', transition: 'all 0.7s ease-in-out' }}
                timeout={1800}
              >
                <Card
                  onMouseEnter={() => dispatch(setHoveredCardIndex(1))}
                  onMouseLeave={() => dispatch(setHoveredCardIndex(null))}
                  sx={{ boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)", height: 180, display: 'flex', flexDirection: 'column', borderRadius: 4, position: 'relative', overflow: 'hidden', transition: 'all 0.3s ease-in-out', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(180deg, #1E3A8A 0%, #00BCD4 100%)', opacity: 0, transition: 'opacity 0.3s ease-in-out', zIndex: 0, borderRadius: 4 }, ':hover::before': { opacity: 1 }, ':hover': { color: 'white', '& .MuiTypography-root': { color: 'white !important', transition: 'color 0.3s ease-in-out' }, '& .MuiCardContent-root': { position: 'relative', zIndex: 1 } }, '& .MuiCardContent-root': { position: 'relative', zIndex: 1 } }}
                  title="Click to view"
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', px: isCustom ? 2 : 3, pt: 2.5 }}>
                    <Typography variant="body1" sx={{ mb: -2.5, fontWeight: 600, color: 'text.primary', mt: 0 }}>
                      NC Status & Backlog
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 3, gap: 2, marginLeft: !isCustom && 0.5, marginRight: !isCustom && 0.5 }}>
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography onClick={() => (dashboardData?.openNcrs || 0) > 0 && handleOpenNcrsClick()} variant="h4" sx={{ fontWeight: 700, fontSize: '1.25rem', color: '#059669', cursor: (dashboardData?.openNcrs || 0) > 0 ? 'pointer' : 'default' }} title={(dashboardData?.openNcrs || 0) > 0 ? 'Click to view' : ''}>
                            <span
                              ref={(el) => {
                                if (el) odometerRefs.current[1] = el;
                              }}
                            >
                              0
                            </span>
                            <span> Open</span>
                          </Typography>
                        </Box>
                        <Box sx={{ width: '1px', height: 50, bgcolor: 'grey.300' }} />
                        <Box sx={{ flex: 1, textAlign: 'right' }}>
                          <Typography onClick={() => (dashboardData?.closedNcrs || 0) > 0 && handleClosedNcrsClick()} variant="h4" sx={{ fontWeight: 400, fontSize: '1.25rem', color: '#9ca3af', cursor: (dashboardData?.closedNcrs || 0) > 0 ? 'pointer' : 'default' }} title={(dashboardData?.closedNcrs || 0) > 0 ? 'Click to view' : ''}>
                            <span
                              ref={(el) => {
                                if (el) odometerRefs.current[2] = el;
                              }}
                            >
                              0
                            </span>
                            <span> Closed</span>
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ width: '100%', height: '1px', bgcolor: 'grey.300', mt: 2, mb: 1.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Typography onClick={() => (dashboardData?.inAction || 0) > 0 && handleInActionClick()} variant="caption" sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "14px", color: 'text.secondary', cursor: (dashboardData?.inAction || 0) > 0 ? 'pointer' : 'default' }} title={(dashboardData?.inAction || 0) > 0 ? 'Click to view' : ''}>
                          <span>{'• '}</span>
                          <span
                            ref={(el) => {
                              if (el) odometerRefs.current[3] = el;
                            }}
                          >
                            0
                          </span>
                          <span>{' Action'}</span>
                        </Typography>
                        <Typography onClick={() => (dashboardData?.inReview || 0) > 0 && handleInReviewClick()} variant="caption" sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "14px", color: 'text.secondary', cursor: (dashboardData?.inReview || 0) > 0 ? 'pointer' : 'default' }} title={(dashboardData?.inReview || 0) > 0 ? 'Click to view' : ''}>
                          <span>{'• '}</span>
                          <span
                            ref={(el) => {
                              if (el) odometerRefs.current[4] = el;
                            }}
                          >
                            0
                          </span>
                          <span>{' Review'}</span>
                        </Typography>
                        <Typography onClick={() => (dashboardData?.inClosure || 0) > 0 && handleInClosureClick()} variant="caption" sx={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "14px", color: 'text.secondary', cursor: (dashboardData?.inClosure || 0) > 0 ? 'pointer' : 'default' }} title={(dashboardData?.inClosure || 0) > 0 ? 'Click to view' : ''}>
                          <span>{'• '}</span>
                          <span
                            ref={(el) => {
                              if (el) odometerRefs.current[5] = el;
                            }}
                          >
                            0
                          </span>
                          <span>{' closure'}</span>
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grow>

          {/* Action Aging */}
          <Grow in={true} timeout={1700}>
            <Grid item xs={12} md={12} lg={4} xl={3}>
              <Grow
                in={true}
                style={{ transformOrigin: '0 0 0' }}
                timeout={1500}
              >
                <Card
                  onMouseEnter={() => dispatch(setHoveredCardIndex(2))}
                  onMouseLeave={() => dispatch(setHoveredCardIndex(null))}
                  sx={{ boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)", height: 180, display: 'flex', flexDirection: 'column', borderRadius: 4, position: 'relative', overflow: 'hidden', transition: 'all 0.3s ease-in-out', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(180deg, #1E3A8A 0%, #00BCD4 100%)', opacity: 0, transition: 'opacity 0.3s ease-in-out', zIndex: 0, borderRadius: 4 }, ':hover::before': { opacity: 1 }, ':hover': { color: 'white', '& .MuiTypography-root': { color: 'white !important', transition: 'color 0.3s ease-in-out' }, '& .MuiCardContent-root': { position: 'relative', zIndex: 1 } }, '& .MuiCardContent-root': { position: 'relative', zIndex: 1 }, cursor: dashboardData?.actionAgingCount > 0 ? 'pointer' : 'default' }}
                  title={dashboardData?.actionAgingCount > 0 ? 'Click to view' : ''}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pt: 2.5 }}>
                    <Typography variant="body1" sx={{ mb: 3, fontWeight: 600, color: 'text.primary', mt: -1 }}>
                      NC in Action Aging {'(Overdue)'}
                    </Typography>
                    <Typography
                      variant="h1" sx={{ fontWeight: 700, fontSize: '4rem', color: '#2563eb', cursor: dashboardData?.actionAgingCount > 0 ? 'pointer' : 'default' }} onClick={() => dashboardData?.actionAgingCount > 0 && handleActionAgingClick()}>
                      <span
                        ref={(el) => {
                          if (el) odometerRefs.current[6] = el;
                        }}
                      >
                        0
                      </span>
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grow>

          {/* Preview Aging */}
          <Grow in={true} timeout={2000}>
            <Grid item xs={12} md={12} lg={4} xl={3}>
              <Grow
                in={true}
                style={{ transformOrigin: '0 0 0', transition: 'all 0.7s ease-in-out' }}
                timeout={2200}
              >
                <Card
                  onMouseEnter={() => dispatch(setHoveredCardIndex(3))}
                  onMouseLeave={() => dispatch(setHoveredCardIndex(null))}
                  sx={{ boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)", height: 180, display: 'flex', flexDirection: 'column', borderRadius: 4, position: 'relative', overflow: 'hidden', transition: 'all 0.3s ease-in-out', '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(180deg, #1E3A8A 0%, #00BCD4 100%)', opacity: 0, transition: 'opacity 0.3s ease-in-out', zIndex: 0, borderRadius: 4 }, ':hover::before': { opacity: 1 }, ':hover': { color: 'white', '& .MuiTypography-root': { color: 'white !important', transition: 'color 0.3s ease-in-out' }, '& .MuiCardContent-root': { position: 'relative', zIndex: 1 } }, '& .MuiCardContent-root': { position: 'relative', zIndex: 1 }, cursor: dashboardData?.reviewAgingCount > 0 ? 'pointer' : 'default' }}
                  title={dashboardData?.reviewAgingCount > 0 ? 'Click to view' : ''}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', pt: 2.5 }}>
                    <Typography variant="body1" sx={{ mb: 3, fontWeight: 600, color: 'text.primary', mt: -1 }}>
                      NC in Review Aging {'(Overdue)'}
                    </Typography>
                    <Typography
                      variant="h1" sx={{ fontWeight: 700, fontSize: '4rem', color: '#00BCD4', cursor: dashboardData?.reviewAgingCount > 0 ? 'pointer' : 'default' }} onClick={() => dashboardData?.reviewAgingCount > 0 && handleReviewAgingClick()}>
                      <span
                        ref={(el) => {
                          if (el) odometerRefs.current[7] = el;
                        }}
                      >
                        0
                      </span>
                    </Typography>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grow>
        </Grid>

        {/* First Row */}
        <Grid container columns={12} spacing={isCustom ? 2 : 4} sx={{ marginBottom: '24px' }}>
          {/* NC Trend */}
          <NCTrendChart monthlyNCRData={monthlyNCRData} monthlyCOPQData={monthlyCOPQData} isCheckCOPQ={isCheckCOPQ} checkData={checkData} handleMonthlyClick={handleMonthlyClick} />

          {/* NC Location Chart */}
          <LocationChart locationData={locationData} handleLocationClick={handleLocationClick} />

          {/* NC Category Chart */}
          <CategoryChart categoryData={categoryData} handleCategoryClick={handleCategoryClick} />

          {/* NC Containment Chart */}
          <ContainmentChart containmentData={containmentData} />

          {/* NC Root Cause Chart */}
          <RootCauseChart rootCauseData={rootCauseData} />

          {/* NC Repeated Items */}
          <Grow in={true} timeout={2000}>
            <Grid item xs={12} md={12} lg={6} xl={4}>
              <Grow
                in={true}
                style={{ transformOrigin: '0 0 0', transition: 'all 0.7s ease-in-out' }}
                timeout={3900}
              >
                <Card sx={{ boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)", height: 380, display: 'flex', flexDirection: 'column', borderRadius: '2%' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', my: 1 }}>
                      Recurrence Analysis
                    </Typography>
                    {repeatedItemsData?.some(i => i.count > 0) ?
                      <>
                        <Box sx={{ display: 'flex', alignItems: 'start', height: '80%', pt: 2 }}>
                          <Box sx={{ height: '100%', flexShrink: 0, display: 'flex', alignItems: 'stretch' }}>
                            <Box
                              sx={{
                                position: 'relative',
                                height: '90%',
                                width: 8,
                                borderRadius: 4,
                                mr: 1.5,
                                background: 'linear-gradient(to bottom, #22c55e 0%, #84cc16 25%, #eab308 50%, #f97316 75%, #ef4444 100%)',
                              }}
                            />

                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '90%', mr: 3 }}>
                              {repeatedItemsData.map((item) => (
                                <Typography
                                  key={item.label}
                                  variant="body1"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'text.primary',
                                    mt: -1,
                                    height: 24,
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '1rem',
                                  }}
                                >
                                  {item.label}
                                </Typography>
                              ))}
                            </Box>

                            <Box sx={{ height: '90%', width: '1px', bgcolor: 'grey.300', mr: 2 }} />
                          </Box>

                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '90%', borderRadius: '2%' }}>
                            {repeatedItemsData.map((item, index) => (
                              <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', height: 32, mt: -1 }}>
                                <Box sx={{ width: '100%' }}>
                                  <RepeatedItemsBar
                                    label={item.label}
                                    count={item.count}
                                    ncrs={item.ncrs}
                                    index={index}
                                  />
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                        <Typography variant="body2" sx={{ fontSize: "12px", textAlign: 'center', mt: 0.5, fontWeight: 400, cursor: 'default', userSelect: 'none' }}>
                          Count NCs
                        </Typography>
                      </>
                      :
                      <NoRecords />
                    }
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          </Grow>
        </Grid>
      </Box>
    </div>
  );
}